import useFormatter from "@/hooks/use-formatter.hook.ts";
import useGoalsHook, { GoalCompositeStatus, GoalStatusColor } from "@/hooks/use-goals.hook.tsx";
import { GoalsTable } from "@/pages/talent/goals/components/GoalsTable.tsx";
import { GoalKeyResults } from "@/pages/talent/goals/details/components/GoalKeyResults.tsx";
import { GoalUpdates } from "@/pages/talent/goals/details/components/GoalUpdates.tsx";
import { useGoalsTableHook } from "@/pages/talent/goals/hooks/use-goals-table.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { GoalKeyResultEditModal } from "@/pages/talent/goals/widgets/GoalKeyResultEditModal.tsx";
import { useGoal, useGoalsSearch } from "@/queries/goals.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiGoal,
  ApiGoalOwnerType,
  ApiGoalSearchFilter,
  ApiGoalStatus,
} from "@/types/goals.types.ts";
import {
  Avatar,
  Box,
  Button,
  DotSeparator,
  EmptyStateBase,
  FontAwesomeIcon,
  HtmlViewer,
  ListItem,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

function SubGoalsTable({
  subGoals,
  isLoading,
  handleAddSubGoal,
}: {
  subGoals: ApiGoal[];
  isLoading: boolean;
  handleAddSubGoal: () => void;
}) {
  const {
    flatGoals,
    expandedPaths,
    loadingPaths,
    toggleExpand,
    handleEditGoal,
    handleDetailsGoal,
    handleDeleteGoal,
  } = useGoalsTableHook(subGoals);
  const { t } = useTranslation();

  return (
    <GoalsTable
      flatGoals={flatGoals}
      isLoading={isLoading}
      expandedPaths={expandedPaths}
      loadingPaths={loadingPaths}
      toggleExpand={toggleExpand}
      handleEditGoal={handleEditGoal}
      handleDetailsGoal={handleDetailsGoal}
      handleDeleteGoal={handleDeleteGoal}
      emptyStateComponent={
        <EmptyStateBase
          emoji={":dart:"}
          title={t("goals:subgoals_empty_title")}
          description={t("goals:goals_empty_state_description")}
          primaryButton={
            <Button onClick={handleAddSubGoal}>{t("goals:subgoals_empty_add")}</Button>
          }
        />
      }
    />
  );
}

export interface GoalDetailsContentProps {
  goalId: number;
  compact?: boolean;
  hideSubgoals?: boolean;
}

export function GoalDetailsContent({
  goalId,
  compact = false,
  hideSubgoals = false,
}: GoalDetailsContentProps) {
  const { t } = useTranslation();
  const { renderTimeAgo, roundPercent, renderEmployeeName, getOverdueDays } = useFormatter();
  const {
    getOwnerTitle,
    getOwnerTypeTitle,
    getGoalStatus,
    getGoalConfidenceLevel,
    renderPeriod,
    renderAssignToAvatar,
    getOwnerName,
  } = useGoalsHook();
  const { openModal } = useModals();

  const { data: goal, isLoading } = useGoal(goalId, !!goalId);

  const subGoalsFilter: ApiGoalSearchFilter = {
    parent_goal_id: goalId ? [goalId] : [],
  } as ApiGoalSearchFilter;
  const { data: subGoalsData, isLoading: subGoalsLoading } = useGoalsSearch(
    subGoalsFilter,
    !!goalId && !hideSubgoals
  );
  const subGoals = subGoalsData?.items ?? [];

  const renderStatus = (compositeStatus: GoalCompositeStatus) => {
    const textSizeClass = "text-lg font-semibold";
    switch (compositeStatus.color) {
      case GoalStatusColor.green:
      case GoalStatusColor.full_green:
        return <Text className={`${textSizeClass} text-positive`}>{compositeStatus.label}</Text>;
      case GoalStatusColor.orange:
        return <Text className={`${textSizeClass} text-warning`}>{compositeStatus.label}</Text>;
      case GoalStatusColor.red:
        return <Text className={`${textSizeClass} text-danger`}>{compositeStatus.label}</Text>;
      case GoalStatusColor.gray:
        return <Text className={`${textSizeClass} text-secondary`}>{compositeStatus.label}</Text>;
      default:
        return null;
    }
  };

  const handleAddMetric = () => {
    if (!goal) return;
    openModal(GoalKeyResultEditModal, { goalId: goal.id });
  };

  const handleAddSubGoal = () => {
    if (!goal) return;
    openModal(GoalEditModal, {
      parentGoal: goal,
      owner_employee: goal.owner_employee ?? undefined,
      owner_department: goal.owner_department ?? undefined,
      owner_team: goal.owner_team ?? undefined,
      owner_company: goal.owner_company ?? undefined,
    });
  };

  if (isLoading) {
    return (
      <Stack gap="lg" className="container-sm m-auto">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </Stack>
    );
  }

  if (!goal) {
    return (
      <Stack className="p-4 items-center justify-center flex-1">
        <EmptyStateBase
          emoji={":mag_right:"}
          title={t("goals:goal_not_found")}
          description={t("goals:goal_not_found_description")}
        />
      </Stack>
    );
  }

  const canOpenAssigneeProfile = !!goal.assign_to && !goal.assign_to.is_archived;
  const assigneeProfileLink = goal.assign_to ? `/employees/${goal.assign_to.id}` : undefined;
  const handleAssigneeClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!goal.assign_to || !canOpenAssigneeProfile) return;

    if (event.metaKey || event.ctrlKey) {
      return;
    }

    event.preventDefault();
    openModal(EmployeeViewModal, {
      employeeId: goal.assign_to.id,
    });
  };

  return (
    <>
      <Stack className="container-sm mx-auto pt-11">
        <Stack gap="smd">
          <Stack>
            <Stack className="pb-5">
              <Stack direction="horizontal" items="start" gap="sm" justify="start">
                {!goal.is_shared && (
                  <FontAwesomeIcon icon="fa-light fa-lock" className="text-3xl" />
                )}
                <Title header={goal.title} size="lg" />
              </Stack>

              <DotSeparator className="pt-1 text-lg">
                {goal.status === ApiGoalStatus.active
                  ? getOverdueDays(goal.date_end) > 0
                    ? renderStatus({
                        label: t("goals:overdue", {
                          days: t("plurals:plural_day", { count: getOverdueDays(goal.date_end) }),
                        }),
                        color: GoalStatusColor.orange,
                      })
                    : renderStatus(getGoalConfidenceLevel(goal))
                  : renderStatus(getGoalStatus(goal))}
                <Text>{renderPeriod(goal.date_start, goal.date_end)}</Text>
                <Text>
                  {t("goals:last_updated", { time: renderTimeAgo(goal.datetime_updated_at) })}
                </Text>
              </DotSeparator>
            </Stack>

            <Box className="h-1 bg-dark/20 relative w-full rounded-xs overflow-hidden">
              <Box
                className="h-1 absolute max-w-full bg-dark rounded-xs"
                style={{ width: `${goal.progress ? goal.progress * 100 : 0}%` }}
              />
            </Box>

            <ListItem
              icon={renderAssignToAvatar(goal)}
              title={goal.assign_to ? renderEmployeeName(goal.assign_to) : "-"}
              caption={goal.assign_to ? getEmployeePositionTitle(goal.assign_to) : ""}
              behavior="list"
              to={canOpenAssigneeProfile ? assigneeProfileLink : undefined}
              onClick={canOpenAssigneeProfile ? handleAssigneeClick : undefined}
              valueSlot={
                <Stack className="text-right" items="end">
                  {goal.owner_type !== ApiGoalOwnerType.employee && getOwnerTitle(goal)}
                  <Text className="text-secondary">{getOwnerTypeTitle(goal.owner_type)}</Text>
                </Stack>
              }
            />
          </Stack>
        </Stack>

        <ListItem
          emphasis="middle"
          preTitle={t("common:progress")}
          valueSlot={
            <Tooltip>
              <TooltipTrigger>
                <Text>{`${goal.progress ? Math.round(goal.progress * 10000) / 100 : 0}%`}</Text>
              </TooltipTrigger>
              <TooltipContent>
                <Stack className="text-sm">
                  <Text className="font-semibold">{t("goals:metrics")}:</Text>
                  {goal.goal_key_results.length > 0
                    ? goal.goal_key_results.map((keyResult) => (
                        <Text key={`key-result-progress-${keyResult.id}`}>
                          {keyResult.title} -&nbsp;
                          <Text className="font-semibold">{roundPercent(keyResult.progress)}%</Text>
                        </Text>
                      ))
                    : t("goals:no_metrics")}
                  {!hideSubgoals && (
                    <>
                      <Text className="font-semibold">
                        <br />
                        {t("goals:sub_goals")}:
                      </Text>
                      {subGoalsLoading
                        ? t("common:loading")
                        : subGoals.length > 0
                          ? subGoals.map((subGoal) => (
                              <Text key={`subgoal-progress-${subGoal.id}`}>
                                {subGoal.title} -&nbsp;
                                <Text className="font-semibold">
                                  {roundPercent(subGoal.progress)}%
                                </Text>
                              </Text>
                            ))
                          : t("goals:sub_goals_empty")}
                    </>
                  )}
                </Stack>
              </TooltipContent>
            </Tooltip>
          }
        />

        {goal.description && (
          <ListItem
            emphasis="middle"
            preTitle={t("common:description")}
            titleSlot={
              <Text>
                <HtmlViewer content={goal.description} />
              </Text>
            }
          />
        )}

        <GoalKeyResults
          keyResults={goal.goal_key_results}
          onAddMetric={handleAddMetric}
          goal={goal}
          compact={compact}
        />

        {goal.goal_key_results.length > 0 && <GoalUpdates goal={goal} goalId={goal.id} />}

        {!hideSubgoals && compact ? (
          subGoals.length > 0 && (
            <>
              <Title
                header={
                  `${t("goals:sub_goals")}` +
                  (subGoalsData?.metadata.total_items
                    ? ` (${subGoalsData?.metadata.total_items})`
                    : "")
                }
                size="md"
                paddingTop
              />
              <Stack gap="sm">
                {subGoals.map((subGoal) => (
                  <Link key={subGoal.id} to={`/goals/${subGoal.id}`} target="_blank">
                    <ListItem
                      icon={
                        subGoal.assign_to ? (
                          renderAssignToAvatar(subGoal)
                        ) : (
                          <Avatar
                            size="sm"
                            icon={<FontAwesomeIcon icon="fa-light fa-bullseye" />}
                          />
                        )
                      }
                      titleSlot={
                        <Text
                          className={
                            subGoal.status !== ApiGoalStatus.active
                              ? "line-through text-secondary"
                              : ""
                          }
                        >
                          {subGoal.title}
                        </Text>
                      }
                      captionSlot={
                        getOwnerName(subGoal) && (
                          <Stack direction={"horizontal"}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Text className="text-dark/40">@{getOwnerName(subGoal)}</Text>
                              </TooltipTrigger>
                              <TooltipContent>
                                {getOwnerTypeTitle(subGoal.owner_type)}
                              </TooltipContent>
                            </Tooltip>
                          </Stack>
                        )
                      }
                    />
                  </Link>
                ))}
              </Stack>
            </>
          )
        ) : (
          <></>
        )}
      </Stack>
      {!hideSubgoals && !compact && (
        <>
          <Stack direction="horizontal" items="center" justify="between" className="pt-8">
            <Title
              header={
                `${t("goals:sub_goals")}` +
                (subGoalsData?.metadata.total_items
                  ? ` (${subGoalsData?.metadata.total_items})`
                  : "")
              }
            />
            <Button variant="secondary" onClick={handleAddSubGoal}>
              {t("common:add")}
            </Button>
          </Stack>
          <SubGoalsTable
            subGoals={subGoals}
            isLoading={subGoalsLoading}
            handleAddSubGoal={handleAddSubGoal}
          />
        </>
      )}
    </>
  );
}
